import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Button, Divider, Grid, Header, Segment } from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { closeModal, openModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import FormParty from "../../../app/common/form/FormParty";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import MyTextInput from "../../../app/common/form/MyTextInput";
import {
  addTransactionToDb,
  addPartyToDb,
  updateTransFieldsInDb,
} from "../../../app/firestore/firestoreService";
import { convertFullName } from "../../../app/common/util/util";

export default function DualAgentCreateBuyerModal({ sellerTransaction }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { people } = useSelector((state) => state.people);
  const { forms } = useSelector((state) => state.doc);
  const { parties } = useSelector((state) => state.transaction);
  const [clientSecondaryExistsField, setClientSecondaryExistsField] =
    useState(false);
  const [clientIsTrustField, setClientIsTrustField] = useState(false);
  const [clientSecondaryIsTrustField, setClientSecondaryIsTrustField] =
    useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const initialValues = {
    client: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      isTrust: false,
      entityName: "",
      authorityTitle: "",
      signerName: "",
    },
    clientSecondary: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      isTrust: false,
      entityName: "",
      authorityTitle: "",
      signerName: "",
      exists: false,
      sharesEmailWithPrimary: false,
    },
  };

  const validationSchema = Yup.object().shape({
    client: Yup.object().shape({
      email: Yup.string().email("Must be a valid email"),
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
    }),
  });

  function handleClose() {
    dispatch(
      closeModal({
        modalType: "DualAgentCreateBuyerModal",
      })
    );
  }

  return (
    <ModalWrapper size="large">
      <Segment clearing>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={async (values, { setSubmitting }) => {
            // Initialize progress steps
            let currentSteps = [
              {
                label: "Create Buyer Transaction",
                status: "pending",
                error: null,
              },
              {
                label: "Add you as Listing Agent to Buyer Transaction",
                status: "pending",
                error: null,
              },
              {
                label: "Add you as Buyer Agent to Seller Transaction",
                status: "pending",
                error: null,
              },
              {
                label: "Copy Title Company into Buyer Transaction",
                status: "pending",
                error: null,
              },
              {
                label: "Copy Seller Clients into Buyer Transaction",
                status: "pending",
                error: null,
              },
              {
                label: "Copy Buyer Clients into Seller Transaction",
                status: "pending",
                error: null,
              },
              {
                label: "Link Buyer Transaction to Seller Transaction",
                status: "pending",
                error: null,
              },
              {
                label: "Link Seller Transaction to Buyer Transaction",
                status: "pending",
                error: null,
              },
            ];

            // Close current modal and open progress modal
            dispatch(closeModal({ modalType: "DualAgentCreateBuyerModal" }));
            dispatch(
              openModal({
                modalType: "DualAgentProgressModal",
                modalProps: { steps: currentSteps, isComplete: false },
              })
            );

            const updateStep = (index, status, error = null) => {
              currentSteps = currentSteps.map((step, i) =>
                i === index ? { ...step, status, error } : step
              );
              dispatch(
                openModal({
                  modalType: "DualAgentProgressModal",
                  modalProps: { steps: currentSteps, isComplete: false },
                })
              );
            };

            const completeProgress = () => {
              dispatch(
                openModal({
                  modalType: "DualAgentProgressModal",
                  modalProps: { steps: currentSteps, isComplete: true },
                })
              );
            };

            try {
              // Step 1: Create buyer transaction data
              updateStep(0, "loading");
              const buyerTransactionData = {
                active: true,
                address: {
                  street: sellerTransaction.address?.street || "",
                  unit: sellerTransaction.address?.unit || "",
                  city: sellerTransaction.address?.city || "",
                  state: sellerTransaction.address?.state || "",
                  zipcode: sellerTransaction.address?.zipcode || "",
                },
                agentRepresents: "Buyer",
                client: {
                  ...values.client,
                  signerName: convertFullName(values.client) || "",
                  exists: true,
                },
                clientSecondaryExists: values.clientSecondary?.exists || false,
                clientSecondary: values.clientSecondary
                  ? {
                      ...values.clientSecondary,
                      signerName: convertFullName(values.clientSecondary) || "",
                      exists: true,
                    }
                  : { exists: false },
                clientThird: { exists: false },
                clientThirdExists: false,
                hasAssistant: false,
                assistant: {},
                managerId: sellerTransaction.agentProfile?.managerId
                  ? sellerTransaction.agentProfile?.managerId
                  : currentUserProfile.managerId || "",
                title: `${values.client.lastName} - ${
                  sellerTransaction.address?.street || ""
                }`,
                pic: sellerTransaction.pic || "",
                propertyDetails: sellerTransaction.propertyDetails
                  ? { ...sellerTransaction.propertyDetails }
                  : {},
                timeZone: sellerTransaction.timeZone || "America/Denver",
                linkedTransactionId: sellerTransaction.id,
                isLinked: true,
              };

              // Create the buyer transaction
              const newBuyerTransactionId = await addTransactionToDb(
                buyerTransactionData,
                people,
                forms,
                currentUserProfile,
                null, // mlsData
                [], // agentsForAssistant
                null, // formTemplates
                [], // taskTemplates
                sellerTransaction.agentProfile
                  ? sellerTransaction.agentProfile
                  : null // agentUserProfile
              );

              updateStep(0, "success");

              // Step 2: Add Listing Agent (from seller transaction) to buyer transaction
              updateStep(1, "loading");
              const listingAgentParty = {
                firstName: sellerTransaction.agentProfile?.firstName || "",
                lastName: sellerTransaction.agentProfile?.lastName || "",
                email: sellerTransaction.agentProfile?.email || "",
                phone: sellerTransaction.agentProfile?.phone || "",
                companyName:
                  sellerTransaction.agentProfile?.brokerageName || "",
                brokerLicenseNumber:
                  sellerTransaction.agentProfile?.brokerLicenseNumber || "",
                brokerageLicenseNumber:
                  sellerTransaction.agentProfile?.brokerageLicenseNumber || "",
                brokerageName:
                  sellerTransaction.agentProfile?.brokerageName || "",
                brokerageId: sellerTransaction.agentProfile?.brokerageId || "",
                linkedUserId: sellerTransaction.userId || "",
                managerId: sellerTransaction.managerId || "",
                hasCoAgent: false,
                hasTransactionCoordinator: false,
                isLinked: true,
                isUser: true,
                linkedTransactionId: sellerTransaction.id,
                transactionId: newBuyerTransactionId,
                role: "Listing Agent",
                type: "Party",
              };
              await addPartyToDb(
                listingAgentParty,
                {
                  id: newBuyerTransactionId,
                  userId: sellerTransaction.userId,
                  managerId: sellerTransaction.managerId || "",
                  agentRepresents: "Buyer",
                },
                []
              );
              updateStep(1, "success");

              // Step 3: Add Buyer Agent to Listing transaction
              updateStep(2, "loading");
              const buyerAgentParty = {
                ...listingAgentParty,
                role: "Buyer Agent",
                type: "Party",
                transactionId: sellerTransaction.id,
                linkedTransactionId: newBuyerTransactionId,
              };
              await addPartyToDb(
                buyerAgentParty,
                {
                  id: sellerTransaction.id,
                  userId: sellerTransaction.userId,
                  managerId: sellerTransaction.managerId || "",
                  agentRepresents: sellerTransaction.agentRepresents,
                },
                []
              );
              updateStep(2, "success");

              // Step 4: Add Title Company if it exists in seller transaction
              updateStep(3, "loading");
              const titleCompanyParty = parties?.find(
                (p) => p.role === "Title Company"
              );
              if (titleCompanyParty) {
                const titlePartyForBuyer = {
                  ...titleCompanyParty,
                  id: undefined, // Remove old id so new one is generated
                };
                delete titlePartyForBuyer.id;
                await addPartyToDb(
                  titlePartyForBuyer,
                  {
                    id: newBuyerTransactionId,
                    userId: sellerTransaction.userId,
                    managerId: sellerTransaction.managerId || "",
                    agentRepresents: "Buyer",
                  },
                  []
                );
              }
              updateStep(3, "success");

              // Step 5: Add Seller clients to Buyer transaction as parties
              // (Seller is the other side's client on this new Buyer
              // transaction, so addPartyToDb's agentRepresents-aware
              // sharing guard must see agentRepresents: "Buyer" here to
              // keep them out of the buyer transaction's sharingWith.)
              updateStep(4, "loading");
              if (sellerTransaction.client) {
                const sellerClientParty = {
                  firstName: sellerTransaction.client.firstName || "",
                  middleName: sellerTransaction.client.middleName || "",
                  lastName: sellerTransaction.client.lastName || "",
                  email: sellerTransaction.client.email || "",
                  phone: sellerTransaction.client.phone || "",
                  isTrust: sellerTransaction.client.isTrust || false,
                  entityName: sellerTransaction.client.entityName || "",
                  signerName: sellerTransaction.client.signerName || "",
                  isUser: false,
                  managerId: sellerTransaction.agentProfile?.managerId || "",
                  sharesEmailWithPrimary: false,
                  transactionId: newBuyerTransactionId,
                  role: "Seller",
                  type: "Party",
                };
                // export async function addPartyToDb(party, transaction, allParties) {
                await addPartyToDb(
                  sellerClientParty,
                  {
                    id: newBuyerTransactionId,
                    userId: sellerTransaction.userId,
                    managerId: sellerTransaction.managerId || "",
                    agentRepresents: "Buyer",
                  },
                  []
                );
                // await addTransClientInDb({ id: newBuyerTransactionId, userId: sellerTransaction.userId, managerId: sellerTransaction.managerId || "" }, sellerClientParty);
              }

              if (
                sellerTransaction.clientSecondaryExists &&
                sellerTransaction.clientSecondary?.lastName
              ) {
                const sellerClient2Party = {
                  firstName: sellerTransaction.clientSecondary.firstName || "",
                  middleName:
                    sellerTransaction.clientSecondary.middleName || "",
                  lastName: sellerTransaction.clientSecondary.lastName || "",
                  email: sellerTransaction.clientSecondary.email || "",
                  phone: sellerTransaction.clientSecondary.phone || "",
                  isTrust: sellerTransaction.clientSecondary.isTrust || false,
                  entityName:
                    sellerTransaction.clientSecondary.entityName || "",
                  signerName:
                    sellerTransaction.clientSecondary.signerName || "",
                  managerId: sellerTransaction.agentProfile?.managerId || "",
                  transactionId: newBuyerTransactionId,
                  isUser: false,
                  role: "Seller 2",
                  type: "Party",
                };
                await addPartyToDb(
                  sellerClient2Party,
                  {
                    id: newBuyerTransactionId,
                    userId: sellerTransaction.userId,
                    managerId: sellerTransaction.managerId || "",
                    agentRepresents: "Buyer",
                  },
                  []
                );
                // await addTransClientInDb({ id: newBuyerTransactionId, userId: currentUserProfile.userId, managerId: currentUserProfile.managerId || "" }, sellerClient2Party);
              }
              updateStep(4, "success");

              // Step 6: Add Buyer clients as parties to Seller transaction
              // Note: the buyer client(s) were already created as parties on
              // the buyer transaction itself back in Step 1 (addTransactionToDb
              // creates a "Buyer"/"Buyer 2" party for transaction.client /
              // clientSecondary). Calling addTransClientInDb again here would
              // create duplicate "Buyer" party records on the buyer
              // transaction, so this step only needs to add the buyer(s) as
              // an "Other Party" on the seller's transaction. Buyer is the
              // other side's client on the Seller's transaction, so
              // agentRepresents: "Seller" must be passed here for
              // addPartyToDb's sharing guard to keep them out of the seller
              // transaction's sharingWith (they should not see this
              // transaction in their transaction list).
              updateStep(5, "loading");
              const buyerClientRole = "Buyer";
              const buyerPartyForSellerSide = {
                ...values.client,
                isUser: false,
                managerId: sellerTransaction.managerId || "",
                transactionId: sellerTransaction.id,
                role: buyerClientRole,
                type: "Party",
              };
              await addPartyToDb(
                buyerPartyForSellerSide,
                {
                  id: sellerTransaction.id,
                  userId: sellerTransaction.userId,
                  managerId: sellerTransaction.managerId || "",
                  agentRepresents: sellerTransaction.agentRepresents,
                },
                []
              );

              if (
                values.clientSecondary.exists &&
                values.clientSecondary.lastName
              ) {
                const buyerParty2ForSellerSide = {
                  ...values.clientSecondary,
                  isUser: false,
                  managerId: sellerTransaction.managerId || "",
                  transactionId: sellerTransaction.id,
                  role: "Buyer 2",
                  type: "Party",
                };

                await addPartyToDb(
                  buyerParty2ForSellerSide,
                  {
                    id: sellerTransaction.id,
                    userId: sellerTransaction.userId,
                    managerId: sellerTransaction.managerId || "",
                    agentRepresents: sellerTransaction.agentRepresents,
                  },
                  []
                );
              }
              updateStep(5, "success");

              // Step 7: Link Buyer Transaction to Seller Transaction
              updateStep(6, "loading");
              await updateTransFieldsInDb(newBuyerTransactionId, {
                linkedTransactionId: sellerTransaction.id,
              });
              updateStep(6, "success");

              // Step 8: Link Seller Transaction to Buyer Transaction
              updateStep(7, "loading");
              await updateTransFieldsInDb(sellerTransaction.id, {
                linkedTransactionId: newBuyerTransactionId,
              });
              updateStep(7, "success");

              toast.success(
                "Buyer transaction created and linked successfully"
              );
              completeProgress();
              if (isMountedRef.current) {
                setSubmitting(false);
              }
            } catch (error) {
              toast.error(error.message);
              console.log(error.message);
              // Mark current step as error
              const currentStepIndex = currentSteps.findIndex(
                (s) => s.status === "loading"
              );
              if (currentStepIndex !== -1) {
                updateStep(currentStepIndex, "error", error.message);
              }
              completeProgress();
              if (isMountedRef.current) {
                setSubmitting(false);
              }
            }
          }}
        >
          {({ isSubmitting, dirty, isValid, values }) => (
            <Form className="ui form medium margin">
              <Header size="large" color="blue">
                Enter Buyer Client Names
              </Header>
              <Divider />
              <p>
                Enter the buyer client information for the new buyer
                transaction:
              </p>

              <h3 className="text blue">Primary Buyer</h3>
              <FormParty fieldKey="client." newPerson={true} />

              <Grid>
                <Grid.Row className="tiny vertical padding">
                  <Grid.Column width={16}>
                    <MyCheckbox
                      name="client.isTrust"
                      label="The client is a company, trust, or other entity"
                      onClick={() => setClientIsTrustField(!clientIsTrustField)}
                    />
                  </Grid.Column>
                </Grid.Row>
                {clientIsTrustField && (
                  <>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={10}>
                        <MyTextInput
                          name="client.entityName"
                          label="Company, trust, or entity name"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={8}>
                        <MyTextInput
                          name="client.authorityTitle"
                          label="Authority title (e.g., Manager, Trustee, President, Member)"
                          placeholder="Manager"
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </>
                )}
                <Grid.Row className="tiny top padding tiny bottom padding">
                  <Grid.Column width={16}>
                    <MyCheckbox
                      name="clientSecondary.exists"
                      label="There is another buyer for this transaction"
                      onClick={() =>
                        setClientSecondaryExistsField(
                          !clientSecondaryExistsField
                        )
                      }
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              {clientSecondaryExistsField && (
                <>
                  <h3 className="text blue">Secondary Buyer</h3>
                  <FormParty fieldKey="clientSecondary." newPerson={true} />
                  <Grid>
                    <Grid.Row className="tiny vertical padding">
                      <Grid.Column width={16}>
                        <MyCheckbox
                          name="clientSecondary.sharesEmailWithPrimary"
                          label="Client shares email with primary buyer"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="tiny vertical padding">
                      <Grid.Column width={16}>
                        <MyCheckbox
                          name="clientSecondary.isTrust"
                          label="The secondary client is a company, trust, or other entity"
                          onClick={() =>
                            setClientSecondaryIsTrustField(
                              !clientSecondaryIsTrustField
                            )
                          }
                        />
                      </Grid.Column>
                    </Grid.Row>
                    {clientSecondaryIsTrustField && (
                      <>
                        <Grid.Row className="zero top padding small bottom padding">
                          <Grid.Column mobile={16} computer={8}>
                            <MyTextInput
                              name="clientSecondary.entityName"
                              label="Company, trust, or entity name"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row className="zero top padding small bottom padding">
                          <Grid.Column mobile={16} computer={8}>
                            <MyTextInput
                              name="clientSecondary.authorityTitle"
                              label="Authority title (e.g., Manager, Trustee, President, Member)"
                              placeholder="Manager"
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </>
                    )}
                  </Grid>
                </>
              )}

              <Divider />
              <Button
                loading={isSubmitting}
                disabled={!isValid || isSubmitting}
                type="submit"
                floated={isMobile ? null : "right"}
                color="blue"
                content="Create and Link"
                className={isMobile ? "fluid medium bottom margin" : null}
              />
              <Button
                disabled={isSubmitting}
                onClick={handleClose}
                type="button"
                floated={isMobile ? null : "right"}
                content="Cancel"
                className={isMobile ? "fluid" : null}
              />
            </Form>
          )}
        </Formik>
      </Segment>
    </ModalWrapper>
  );
}

